import { Repository } from "./adapters/repository";

import * as retrieveTasks from "./useCases/retrieveTasks";
import * as createTask from "./useCases/createTask";
import * as createKanbanTask from "./useCases/createKanbanTask";
import * as createMilestone from "./useCases/createMilestone";
import * as editTask from "./useCases/editTask";
import * as editMilestone from "./useCases/editMilestone";
import * as editList from "./useCases/editList";
import * as retrieveLists from "./useCases/retrieveLists";
import * as retrieveTask from "./useCases/retrieveTask";
import * as createTaskStatus from "./useCases/createTaskStatus";
import * as editTaskStatus from "./useCases/editTaskStatus";
import * as removeTaskStatus from "./useCases/removeTaskStatus";
import * as retrieveTaskStatuses from "./useCases/retrieveTaskStatuses";
import * as createList from "./useCases/createList";
import * as retrieveProjectGantt from "./useCases/retrieveProjectGantt";
import * as removeTask from "./useCases/removeTask";
import * as retrieveList from "./useCases/retrieveList";
import * as retrieveMilestone from "./useCases/retrieveMilestone";
import * as removeList from "./useCases/removeList";
import * as retrieveKanban from "./useCases/retrieveKanban";
import * as moveListElement from "./useCases/moveListElement";
import * as moveKanbanTask from "./useCases/moveKanbanTask";
import * as markMilestoneAsCompleted from "./useCases/markMilestoneAsCompleted";
import * as markTaskAsCompleted from "./useCases/markTaskAsCompleted";
import * as createEvent from "./useCases/createEvent";
import * as removeMilestone from "./useCases/removeMilestone";
import * as editEvent from "./useCases/editEvent";
import * as retrieveEvent from "./useCases/retrieveEvent";
import * as removeEvent from "./useCases/removeEvent";
import * as duplicateList from "./useCases/duplicateList";
import * as retrieveProjectLogs from "./useCases/retrieveProjectLogs";
import * as retrieveWbsMeta from "./useCases/retrieveWbsMeta";
enum UseCases {
  createList = "createList",
  createEvent = "createEvent",
  editEvent = "editEvent",
  retrieveEvent = "retrieveEvent",
  removeEvent = "removeEvent",
  createMilestone = "createMilestone",
  createTask = "createTask",
  createKanbanTask = "createKanbanTask",
  createTaskStatus = "createTaskStatus",
  editList = "editList",
  editMilestone = "editMilestone",
  editTask = "editTask",
  editTaskStatus = "editTaskStatus",
  markMilestoneAsCompleted = "markMilestoneAsCompleted",
  markTaskAsCompleted = "markTaskAsCompleted",
  moveKanbanTask = "moveKanbanTask",
  moveListElement = "moveListElement",
  removeList = "removeList",
  removeTask = "removeTask",
  removeTaskStatus = "removeTaskStatus",
  retrieveKanban = "retrieveKanban",
  retrieveList = "retrieveList",
  retrieveLists = "retrieveLists",
  retrieveMilestone = "retrieveMilestone",
  retrieveProjectGantt = "retrieveProjectGantt",
  retrieveTask = "retrieveTask",
  retrieveTasks = "retrieveTasks",
  retrieveTaskStatuses = "retrieveTaskStatuses",
  removeMilestone = "removeMilestone",
  duplicateList = "duplicateList",
  retrieveProjectLogs = "retrieveProjectLogs",
  retrieveWbsMeta = "retrieveWbsMeta",
}

interface Core {
  [UseCases.markTaskAsCompleted]: markTaskAsCompleted.Interactor;
  [UseCases.retrieveTasks]: retrieveTasks.Interactor;
  [UseCases.createTask]: createTask.Interactor;
  [UseCases.createKanbanTask]: createKanbanTask.Interactor;
  [UseCases.editTask]: editTask.Interactor;
  [UseCases.retrieveLists]: retrieveLists.Interactor;
  [UseCases.retrieveTask]: retrieveTask.Interactor;
  [UseCases.createTaskStatus]: createTaskStatus.Interactor;
  [UseCases.editTaskStatus]: editTaskStatus.Interactor;
  [UseCases.retrieveTaskStatuses]: retrieveTaskStatuses.Interactor;
  [UseCases.createList]: createList.Interactor;
  [UseCases.retrieveProjectGantt]: retrieveProjectGantt.Interactor;
  [UseCases.removeTask]: removeTask.Interactor;
  [UseCases.retrieveList]: retrieveList.Interactor;
  [UseCases.editList]: editList.Interactor;
  [UseCases.removeList]: removeList.Interactor;
  [UseCases.removeTaskStatus]: removeTaskStatus.Interactor;
  [UseCases.createMilestone]: createMilestone.Interactor;
  [UseCases.retrieveKanban]: retrieveKanban.Interactor;
  [UseCases.editMilestone]: editMilestone.Interactor;
  [UseCases.retrieveMilestone]: retrieveMilestone.Interactor;
  [UseCases.moveListElement]: moveListElement.Interactor;
  [UseCases.moveKanbanTask]: moveKanbanTask.Interactor;
  [UseCases.markMilestoneAsCompleted]: markMilestoneAsCompleted.Interactor;
  [UseCases.createEvent]: createEvent.Interactor;
  [UseCases.removeMilestone]: removeMilestone.Interactor;
  [UseCases.editEvent]: editEvent.Interactor;
  [UseCases.retrieveEvent]: retrieveEvent.Interactor;
  [UseCases.removeEvent]: removeEvent.Interactor;
  [UseCases.duplicateList]: duplicateList.Interactor;
  [UseCases.retrieveProjectLogs]: retrieveProjectLogs.Interactor;
  [UseCases.retrieveWbsMeta]: retrieveWbsMeta.Interactor;
}

type Boundaries = BoundaryMap<Core>;

type Dependencies = {
  repository: Repository;
};

function createCore(dependencies: Dependencies): Core {
  return {
    [UseCases.retrieveTasks]: retrieveTasks.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createTask]: createTask.createInteractor(dependencies.repository),
    [UseCases.createKanbanTask]: createKanbanTask.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createMilestone]: createMilestone.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editTask]: editTask.createInteractor(dependencies.repository),
    [UseCases.editMilestone]: editMilestone.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveLists]: retrieveLists.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveTask]: retrieveTask.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createTaskStatus]: createTaskStatus.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editTaskStatus]: editTaskStatus.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveTaskStatuses]: retrieveTaskStatuses.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createList]: createList.createInteractor(dependencies.repository),
    [UseCases.retrieveProjectGantt]: retrieveProjectGantt.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeTask]: removeTask.createInteractor(dependencies.repository),
    [UseCases.retrieveList]: retrieveList.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editList]: editList.createInteractor(dependencies.repository),
    [UseCases.removeList]: removeList.createInteractor(dependencies.repository),
    [UseCases.removeTaskStatus]: removeTaskStatus.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveKanban]: retrieveKanban.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveMilestone]: retrieveMilestone.createInteractor(
      dependencies.repository,
    ),
    [UseCases.moveListElement]: moveListElement.createInteractor(
      dependencies.repository,
    ),
    [UseCases.moveKanbanTask]: moveKanbanTask.createInteractor(
      dependencies.repository,
    ),
    [UseCases.markMilestoneAsCompleted]:
      markMilestoneAsCompleted.createInteractor(dependencies.repository),
    [UseCases.markTaskAsCompleted]: markTaskAsCompleted.createInteractor(
      dependencies.repository,
    ),
    [UseCases.createEvent]: createEvent.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeMilestone]: removeMilestone.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editEvent]: editEvent.createInteractor(dependencies.repository),
    [UseCases.retrieveEvent]: retrieveEvent.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeEvent]: removeEvent.createInteractor(
      dependencies.repository,
    ),
    [UseCases.duplicateList]: duplicateList.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveProjectLogs]: retrieveProjectLogs.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveWbsMeta]: retrieveWbsMeta.createInteractor(
      dependencies.repository,
    ),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
